/**
 * SnapshotHistory — elenco snapshot congelati + vista inline frozen
 *
 * Espone funzione pura:
 *   formatSnapshotKpi(kpi_data) → { bookedRevenue: string; attainmentPct: string }
 */
import { Clock, Eye, AlertTriangle } from 'lucide-react'
import { formatKpiEur } from '../../lib/formatters'
import type { BrSnapshot } from '../../hooks/useBusinessReview'

// ---------------------------------------------------------------------------
// Funzione pura — estrazione KPI principali dal kpi_data JSON
// ---------------------------------------------------------------------------

export interface SnapshotKpiDisplay {
  bookedRevenue: string
  attainmentPct: string
}

export function formatSnapshotKpi(
  kpiData: Record<string, unknown>,
): SnapshotKpiDisplay {
  const strategic = kpiData?.strategic as Record<string, unknown> | undefined
  const booked = strategic?.booked_revenue_ytd
  const attainment = strategic?.attainment_pct

  return {
    bookedRevenue: booked != null ? formatKpiEur(Number(booked)) : '—',
    attainmentPct:
      attainment != null
        ? `${Number(attainment).toLocaleString('it-IT', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}%`
        : '—',
  }
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

interface SnapshotHistoryProps {
  snapshots: BrSnapshot[]
  selectedId: string | null
  onSelect: (id: string | null) => void
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function FrozenBanner({ frozenAt }: { frozenAt: string }) {
  return (
    <div
      data-testid="frozen-banner"
      className="flex items-center gap-2 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-2.5 text-sm text-indigo-300"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>
        Snapshot congelato il{' '}
        <strong>{fmtDate(frozenAt)}</strong> — valori non aggiornati
      </span>
    </div>
  )
}

export function SnapshotHistory({
  snapshots,
  selectedId,
  onSelect,
}: SnapshotHistoryProps) {
  const frozen = snapshots.filter((s) => s.is_frozen)

  if (frozen.length === 0) {
    return (
      <div
        data-testid="snapshot-history-empty"
        className="rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] p-5 shadow-[var(--km-shadow-md)]"
      >
        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[--km-text-primary]">
          <Clock className="w-4 h-4 text-indigo-400" />
          Storico review
        </div>
        <p className="text-xs text-[--km-text-secondary]">
          Nessuno snapshot congelato. Usa il pulsante{' '}
          <strong>Salva snapshot</strong> per creare il primo.
        </p>
      </div>
    )
  }

  const selected = frozen.find((s) => s.id === selectedId) ?? null

  return (
    <div
      data-testid="snapshot-history"
      className="space-y-3"
    >
      {/* Frozen view banner — visibile solo se uno snapshot è selezionato */}
      {selected && selected.frozen_at && (
        <FrozenBanner frozenAt={selected.frozen_at} />
      )}

      <div className="rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] shadow-[var(--km-shadow-md)] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--km-card-border)]">
          <Clock className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-semibold text-[--km-text-primary]">Storico review</span>
          <span className="ml-auto text-xs text-[--km-text-secondary]">
            {frozen.length} snapshot
          </span>
        </div>

        <ul data-testid="snapshot-list" className="divide-y divide-[var(--km-card-border)]">
          {frozen.map((snap) => {
            const kpis = formatSnapshotKpi(snap.kpi_data)
            const isActive = snap.id === selectedId

            return (
              <li
                key={snap.id}
                data-testid="snapshot-row"
                className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${
                  isActive
                    ? 'bg-indigo-500/10'
                    : 'hover:bg-white/3'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-[--km-text-primary] truncate">
                      {snap.period_label}
                    </span>
                    <span className="text-[10px] text-[--km-text-secondary]">
                      {fmtDate(snap.frozen_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[--km-text-secondary]">
                    <span data-testid="snap-booked">Booked: {kpis.bookedRevenue}</span>
                    <span>·</span>
                    <span data-testid="snap-attainment">Attainment: {kpis.attainmentPct}</span>
                  </div>
                </div>

                <button
                  type="button"
                  data-testid="snapshot-view-btn"
                  onClick={() => onSelect(isActive ? null : snap.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-indigo-500 text-white'
                      : 'border border-[var(--km-card-border)] text-[--km-text-secondary] hover:text-[--km-text-primary] hover:bg-white/5'
                  }`}
                  aria-label={isActive ? 'Nascondi snapshot' : 'Visualizza snapshot'}
                >
                  <Eye className="w-3.5 h-3.5" />
                  {isActive ? 'Attivo' : 'Vedi'}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
